/**
 * Client-side chat moderation. Honest about scope: nothing here touches the
 * server, so mutes / hides only affect *this* browser's view of the chat
 * log. That's enough for solo dev testing — if/when we want real bans we
 * add a server-side admin op and gate it by wallet signature.
 *
 * State is in-memory only (zustand). On reload, mutes reset — useful so
 * a single tester doesn't accumulate stale rules across runs.
 */
import { create } from "zustand";

interface DevChatModStore {
  /** Lowercased usernames whose messages we hide locally. */
  mutedUsers: Set<string>;
  /** Chat message ids hidden one-off. */
  hiddenIds: Set<string>;
  /** Lowercased substring; messages containing it are highlighted. */
  highlight: string;

  muteUser: (name: string) => void;
  unmuteUser: (name: string) => void;
  hideMessage: (id: string) => void;
  clearHidden: () => void;
  setHighlight: (s: string) => void;
}

export const useDevChatMod = create<DevChatModStore>((set) => ({
  mutedUsers: new Set(),
  hiddenIds: new Set(),
  highlight: "",

  muteUser: (name) =>
    set((s) => {
      const next = new Set(s.mutedUsers);
      next.add(name.toLowerCase());
      return { mutedUsers: next };
    }),
  unmuteUser: (name) =>
    set((s) => {
      const next = new Set(s.mutedUsers);
      next.delete(name.toLowerCase());
      return { mutedUsers: next };
    }),
  hideMessage: (id) =>
    set((s) => {
      const next = new Set(s.hiddenIds);
      next.add(id);
      return { hiddenIds: next };
    }),
  clearHidden: () => set({ hiddenIds: new Set(), mutedUsers: new Set() }),
  setHighlight: (s) => set({ highlight: s.toLowerCase() }),
}));
